import { memo, useState, useRef, useEffect, useLayoutEffect } from 'react';
import ContentEditable from 'react-contenteditable';
import DOMPurify from 'dompurify';
import { Handle, Position } from '@xyflow/react';
import ReactDOM from 'react-dom';
import Draggable from 'react-draggable';
import { ResizableBox } from 'react-resizable';
import 'react-resizable/css/styles.css';
import '../../css/SwimlineNodes.css';
import '../../css/publishnode.css';
import { useTranslation } from '../../hooks/useTranslation';
import { stripHtml } from '../../components/StripHtml';
import ActivityEnergyLayer, {
  getEnergyMeterClass,
} from '../../components/energy-meter/ActivityEnergyLayer';
import { hyphenateText, cleanHyphens } from '../../utils/hyphenateText';

const SwimlaneDiamondNode = ({ id, data, editable, langCode }) => {
  const [title, setTitle] = useState(() =>
    hyphenateText(stripHtml(data?.details?.title || data?.label || ''), langCode)
  );
  const titleRef = useRef(null);
  const [autoFocus, setAutoFocus] = useState(data.autoFocus);
  const [isHovered, setIsHovered] = useState(false);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [popupSize, setPopupSize] = useState({ width: 600, height: 450 });
  const t = useTranslation();

  const nodeId = id || data.id;
  const em = data?.em;
  const emClass = getEnergyMeterClass(em, nodeId);

  useLayoutEffect(() => {
    if (titleRef.current && titleRef.current.scrollTop !== 0) {
      titleRef.current.scrollTop = 0;
    }
  });

  // Sync label
  useEffect(() => {
    const isFocused = titleRef.current && document.activeElement === titleRef.current;
    if (!isFocused) {
      const incoming = data?.details?.title !== undefined ? data?.details?.title : data?.label;
      if (incoming !== undefined) {
        setTitle(hyphenateText(stripHtml(incoming), langCode));
      }
    }
  }, [data?.details?.title, data?.label, langCode]);

  // Handle auto focus in edit mode
  useEffect(() => {
    if (editable && autoFocus && titleRef.current) {
      setTimeout(() => {
        const el = titleRef.current;
        el.focus({ preventScroll: true });

        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);

        setAutoFocus(false);
      }, 0);
    }
  }, [autoFocus, editable]);

  const handleChange = (e) => {
    if (e.target && e.target.scrollTop !== 0) {
      e.target.scrollTop = 0;
    }
    const rawVal = e.target.value;
    setTitle(rawVal);
    const cleanVal = cleanHyphens(rawVal);
    if (data.onTitleChange) {
      data.onTitleChange(cleanVal);
    }
    if (data.onLabelChange) {
      data.onLabelChange(cleanVal);
    }
  };

  const handleBlur = () => {
    const rawVal = titleRef.current
      ? titleRef.current.innerText || titleRef.current.textContent || title
      : title;
    const cleanVal = cleanHyphens(rawVal);
    const hyphenated = hyphenateText(cleanVal, langCode);
    setTitle(hyphenated);
    if (data.onTitleChange) {
      data.onTitleChange(cleanVal);
    }
    if (data.onLabelChange) {
      data.onLabelChange(cleanVal);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      document.execCommand('insertLineBreak');
    }
  };

  const handleBoxClick = (e) => {
    if (editable) {
      if (e.target.classList.contains('nodrag')) {
        return;
      }
      if (titleRef.current && document.activeElement !== titleRef.current) {
        titleRef.current.focus();

        const el = titleRef.current;
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      }
    } else {
      if (em && em.active) {
        e.stopPropagation();
        if (em.mode === 'analysis') {
          em.onOpenPopover({ id: nodeId, title });
        }
        return;
      }
      setIsPopupVisible(true);
    }
  };

  const handleClosePopup = () => {
    setIsPopupVisible(false);
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsPopupVisible(false);
      }
    };
    if (isPopupVisible) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isPopupVisible]);

  const handleFocus = (e) => {
    e.stopPropagation();
  };

  const renderPopup = () => (
    <Draggable handle=".popupHeader" bounds=".scrollbar_wrapper" cancel=".popupContent_content">
      <ResizableBox
        width={popupSize.width}
        height={popupSize.height}
        minConstraints={[300, 200]}
        maxConstraints={[800, 600]}
        onResizeStop={(e, { size }) => setPopupSize(size)}
        style={{
          position: 'absolute',
          top: '12%',
          left: '30%',
          right: '0',
          transform: 'translate(-50%, 0)',
          backgroundColor: '#ffffff',
          border: '1px solid #011f60',
          overflow: 'hidden',
          zIndex: 1001,
          boxShadow: '0 0 10px #011f6047',
        }}
      >
        <div style={{ ...styles.popup, width: '100%', height: '100%' }}>
          <div className="popupHeader" style={styles.popupHeader}>
            <h3 style={styles.popupTitle}>{title}</h3>
            <button style={styles.closeButton} onClick={handleClosePopup}>
              {t('CLOSE')}
            </button>
          </div>
          <div className="ql-snow" style={{ flexGrow: 1, overflowY: 'auto' }}>
            <div
              className="popupContent_content ql-editor rbpm-infobox"
              style={{ ...styles.popupContent, overflowY: 'visible' }}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  data.details?.translations?.[(langCode || 'EN').toUpperCase()]?.content ||
                    data.details?.translations?.[(langCode || 'en').toLowerCase()]?.content ||
                    data.details?.translations?.[langCode]?.content ||
                    data.details?.content ||
                    ''
                ),
              }}
            />
          </div>
        </div>
      </ResizableBox>
    </Draggable>
  );

  const nodebgheight = document.querySelector('.react-flow__node');
  const nodebgheights = nodebgheight ? nodebgheight.getBoundingClientRect().height : 588 / 7 - 0.3;
  const container = document.querySelector('.ss_publish_border');

  return (
    <>
      <div
        className="diamond_Wrapper_custom"
        style={styles.wrapper}
        onClick={handleBoxClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {!editable && <ActivityEnergyLayer data={data} activityId={nodeId} activityTitle={title} />}
        <div style={styles.diamondWrapper} className="diamond_Wrapper">
          <div
            style={{ ...styles.diamond, width: nodebgheights - 10, height: nodebgheights - 10 }}
            className={`diamond_header ${!editable ? emClass : ''}`}
          >
            {editable ? (
              <ContentEditable
                innerRef={titleRef}
                html={title}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onChange={(e) => handleChange({ target: { value: e.target.value } })}
                onKeyDown={handleKeyDown}
                placeholder=""
                style={styles.title}
                className="nodrag rb-process-model-hyphenated-text"
                lang={(langCode || 'de').toLowerCase()}
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <div
                ref={titleRef}
                style={styles.title}
                className="rb-process-model-hyphenated-text"
                lang={(langCode || 'de').toLowerCase()}
              >
                {hyphenateText(
                  stripHtml(data?.details?.title || data?.label || title || ''),
                  langCode
                )}
              </div>
            )}
          </div>
          <Handle
            className={editable ? 'topdot_edit' : 'topdot'}
            type="target"
            position={Position.Top}
            id="top-target"
            style={editable && isHovered ? styles.hoverhandle : styles.handle}
          />
          <Handle
            className={editable ? 'topdot_edit' : 'topdot'}
            type="source"
            position={Position.Top}
            id="top-source"
            style={editable && isHovered ? styles.hoverhandle : styles.handle}
          />

          <Handle
            className={editable ? 'bottomdot_edit' : 'bottomdot'}
            type="target"
            position={Position.Bottom}
            id="bottom-target"
            style={editable && isHovered ? styles.hoverhandle : styles.handle}
          />
          <Handle
            className={editable ? 'bottomdot_edit' : 'bottomdot'}
            type="source"
            position={Position.Bottom}
            id="bottom-source"
            style={editable && isHovered ? styles.hoverhandle : styles.handle}
          />

          <Handle
            className={editable ? 'leftdot_edit' : 'leftdot'}
            type="target"
            position={Position.Left}
            id="left-target"
            style={editable && isHovered ? styles.hoverhandle : styles.handle}
          />
          <Handle
            className={editable ? 'leftdot_edit' : 'leftdot'}
            type="source"
            position={Position.Left}
            id="left-source"
            style={editable && isHovered ? styles.hoverhandle : styles.handle}
          />

          <Handle
            className={editable ? 'rightdot_edit' : 'rightdot'}
            type="target"
            position={Position.Right}
            id="right-target"
            style={editable && isHovered ? styles.hoverhandle : styles.handle}
          />
          <Handle
            className={editable ? 'rightdot_edit' : 'rightdot'}
            type="source"
            position={Position.Right}
            id="right-source"
            style={editable && isHovered ? styles.hoverhandle : styles.handle}
          />
        </div>
      </div>

      {!editable &&
        isPopupVisible &&
        data.details?.title &&
        container &&
        ReactDOM.createPortal(renderPopup(), container)}
    </>
  );
};

const styles = {
  wrapper: {
    position: 'relative',
    width: '80%',
    height: '72%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '0 0 100%',
  },
  diamondWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '0 0 100%',
  },
  diamond: {
    position: 'relative',
    backgroundColor: '#ffffff',
    color: '#002060',
    border: '1.6px solid #002060',
    transform: 'rotate(45deg) scale(0.68)',
    transformOrigin: 'center center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  title: {
    transform: 'rotate(-45deg)',
    transformOrigin: 'center center',
    fontSize: '13px',
    fontFamily: 'Poppins, sans-serif',
    lineHeight: 1.1,
    hyphens: 'auto',
    hyphenateCharacter: '-',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    lineBreak: 'auto',
    whiteSpace: 'pre-wrap',
    textAlign: 'center',
    background: 'transparent',
    border: 'none',
    outline: 'none',
    width: '100%',
    maxWidth: '100%',
    maxHeight: '125%',
    boxSizing: 'border-box',
    padding: 0,
    margin: 'auto',
    color: 'rgb(0, 32, 96)',
    overflow: 'visible',
  },

  hoverhandle: {
    backgroundColor: 'red',
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    position: 'absolute',
    border: 'none',
  },
  handle: {
    backgroundColor: 'transparent',
    position: 'absolute',
    border: 'none',
    width: '8px',
    height: '8px',
    pointerEvents: 'none',
  },
  popup: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    overflowY: 'auto',
    zIndex: 1001,
  },
  popupHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px',
    backgroundColor: 'white',
    cursor: 'move',
  },
  popupTitle: {
    fontSize: '16px',
    fontWeight: '500',
    color: '#011f60',
    margin: '0',
  },
  popupContent: {
    fontSize: '13px',
    color: '#002060',
    padding: '0 10px 10px',
    overflowY: 'auto',
    flexGrow: 1,
  },
  closeButton: {
    background: '#011f60',
    borderRadius: '7px',
    color: '#ffffff',
    cursor: 'pointer',
    fontSize: '12px',
    padding: '2px 20px',
    textTransform: 'capitalize',
    border: '0',
  },
};

export default memo(SwimlaneDiamondNode);
